import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type ConfirmOpts = {
  title: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "danger";
  /** If set, the user must type this exact string to enable Confirm. */
  requireText?: string;
};

type Ctx = (opts: ConfirmOpts) => Promise<boolean>;
const ConfirmContext = createContext<Ctx | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef<(v: boolean) => void>(() => {});

  const confirm = useCallback<Ctx>((o) => {
    setTyped("");
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (v: boolean) => {
    resolver.current(v);
    setOpts(null);
  };

  const gated = opts?.requireText != null && typed.trim() !== opts.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={opts != null} onOpenChange={(o) => !o && close(false)}>
        <DialogContent className="max-w-md">
          {opts && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {opts.variant === "danger" && <AlertTriangle className="size-5 text-destructive" />}
                  {opts.title}
                </DialogTitle>
                {opts.message && <DialogDescription asChild><div>{opts.message}</div></DialogDescription>}
              </DialogHeader>
              {opts.requireText != null && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">
                    Type <b className="text-foreground">{opts.requireText}</b> to confirm.
                  </p>
                  <Input
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !gated) close(true); }}
                    placeholder={opts.requireText}
                  />
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => close(false)}>
                  {opts.cancelText ?? "Cancel"}
                </Button>
                <Button
                  variant={opts.variant === "danger" ? "destructive" : "default"}
                  disabled={gated}
                  onClick={() => close(true)}
                >
                  {opts.confirmText ?? "Confirm"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ctx {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
